import React, { useState, useContext, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../../context/AppContext';
import { motion, AnimatePresence } from 'framer-motion';
import { FaBars, FaTimes, FaMoon, FaSun } from 'react-icons/fa';
import './Navbar.css';

const Navbar = () => {
  const { t } = useTranslation();
  const { language, toggleLanguage, theme, toggleTheme, isRTL } = useContext(AppContext);
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  // Effet pour détecter le scroll
  useEffect(() => {
    const handleScroll = () => {
      const isScrolled = window.scrollY > 20;
      if (isScrolled !== scrolled) {
        setScrolled(isScrolled);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [scrolled]);

  // Fermer le menu mobile lorsqu'on change de page
  useEffect(() => {
    setIsOpen(false);
  }, [location]);

  // Animation pour le menu mobile
  const menuVariants = {
    closed: {
      x: isRTL ? '-100%' : '100%',
      opacity: 0,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    },
    open: {
      x: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  const links = [
    { name: t('nav.home'), path: '/' },
    { name: t('nav.pedagogy'), path: '/pedagogy' },
    { name: t('nav.events'), path: '/events' },
    { name: t('nav.blog'), path: '/blog' },
    { name: t('nav.registration'), path: '/registration' },
    { name: t('nav.about'), path: '/about' },
    { name: t('nav.contact'), path: '/contact' }
  ];

  return (
    <nav className={`navbar ${scrolled ? 'navbar-scrolled' : ''} px-6 py-4 ${isRTL ? 'font-arabic' : 'font-sans'}`}>
      <div className="container mx-auto flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img 
            src={language === 'fr' ? '/image/logofr.png' : '/image/logoar.png'} 
            alt="DNC Logo" 
            className="h-16 w-auto"
          />
        </Link>

        {/* Navigation sur desktop */}
        <div className={`hidden md:flex items-center ${isRTL ? 'space-x-reverse space-x-10' : 'space-x-10'}`}>
          {links.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`nav-link text-gray-700 dark:text-gray-300 hover:text-primary dark:hover:text-primary transition-colors duration-300 ${
                location.pathname === link.path ? 'font-semibold text-primary' : ''
              }`}
            >
              {link.name}
            </Link>
          ))}
        </div>

        {/* Actions (sélecteur de langue et toggle de thème) */}
        <div className="hidden md:flex items-center space-x-4 mr-4 ml-4">
          <button
            onClick={toggleLanguage}
            className="language-button border border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-300"
          >
            {language === 'fr' ?  (
              <>
                <img src="/image/fr.png" alt="Drapeau français" />
                <span>Français</span>
              </>
            ) :(
              <>
                <img src="/image/ar.png" alt="Drapeau tunisien" />
                <span>العربية</span>
              </>
            )}
          </button>
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-gray-800"
            aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
          >
            {theme === 'light' ? <FaMoon className="text-gray-700 text-lg" /> : <FaSun className="text-yellow-300 text-lg" />}
          </button>
        </div>

        {/* Bouton de menu mobile */}
        <button
          className="md:hidden p-2 rounded-md focus:outline-none"
          onClick={() => setIsOpen(!isOpen)}
          aria-label={isOpen ? 'Close menu' : 'Open menu'}
        >
          {isOpen ? <FaTimes className="text-primary h-6 w-6" /> : <FaBars className="text-primary h-6 w-6" />}
        </button>
      </div>

      {/* Menu mobile */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="mobile-menu py-12 px-6"
            initial="closed"
            animate="open"
            exit="closed"
            variants={menuVariants}
          >
            <div className="flex flex-col space-y-8">
              {links.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`text-xl ${
                    location.pathname === link.path ? 'font-semibold text-primary' : 'text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {link.name}
                </Link>
              ))}

              <div className="flex flex-col space-y-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={toggleLanguage}
                  className="language-button border border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-300"
                >
                  {language === 'fr' ? (
                    <>
                      <img src="/image/ar.png" alt="Drapeau tunisien" />
                      <span>العربية</span>
                    </>
                  ) :(
                    <>
                      <img src="/image/fr.png" alt="Drapeau français" />
                      <span>Français</span>
                    </>
                  ) }
                </button>
                <button
                  onClick={toggleTheme}
                  className="px-3 py-2 rounded-md border border-gray-300 dark:border-gray-700 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors duration-300 flex items-center focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-gray-800"
                >
                  {theme === 'light' ? (
                    <>
                      <FaMoon className={`${isRTL ? 'ml-2' : 'mr-2'} text-gray-700`} /> {t('common.darkMode')}
                    </>
                  ) : (
                    <>
                      <FaSun className={`${isRTL ? 'ml-2' : 'mr-2'} text-yellow-300`} /> {t('common.lightMode')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;
